'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { isProUser } from '@/lib/auth';
import { track } from '@/lib/analytics';
import { useUserData } from '@/context/UserDataContext';
import {
  buildCompareVarietiesFallbackExplanation,
  buildCompareVarietiesResult,
  buildFruitPollinationFallbackExplanation,
  buildFruitPollinationResult,
  getFruitPollinationType,
  listFruitPollinationTypes,
  resolveFruitPollinationVarietyByQuery,
  type CompareVarietiesResult,
  type FruitPollinationResult,
  type PollinationAdditionSuggestion,
  type PollinationMode,
  type PollinationPartnerMatch,
  type StoreModePairing,
} from '@/lib/fruitTreePollination';
import type {
  FruitPollinationFruitType,
  FruitPollinationVariety,
} from '@/data/fruitPollination';
import { getFruitVarietyHarvestWindow, getFruitVarietyInsight } from '@/data/fruitVarietyInsight';

const POLLINATION_MODES: Array<{ id: PollinationMode; label: string; shortLabel: string; description: string }> = [
  {
    id: 'single',
    label: 'Single variety',
    shortLabel: 'Single variety',
    description: 'Choose one variety and Willowbottom will show the best pollination partners.',
  },
  {
    id: 'compare',
    label: 'Compare several varieties',
    shortLabel: 'Compare several',
    description: 'Add the varieties you already have or can buy, and Willowbottom will sort the strongest pairings from that list.',
  },
];

/**
 * Long enough to swallow a burst of list edits (adding a variety, ticking
 * "already have this", removing one) without the wait being noticeable against
 * a call that takes seconds.
 */
/** Long enough that a variety typed one letter at a time reports once, at the end. */
const NO_MATCH_REPORT_DELAY_MS = 1200;

/**
 * Roughly how often these get planted in a home garden, so the index below
 * opens with what most people came for. Anything missing sorts alphabetically
 * after these.
 */
const FRUIT_TYPE_DISPLAY_ORDER: FruitPollinationFruitType[] = [
  'apple', 'peach', 'pear', 'cherry', 'plum', 'blueberry',
  'strawberry', 'raspberry', 'blackberry', 'grape', 'fig', 'apricot',
  'nectarine', 'persimmon', 'pomegranate', 'mulberry', 'elderberry', 'quince',
  'serviceberry', 'pawpaw', 'currant', 'gooseberry', 'jujube', 'aronia',
  'honeyberry', 'hazelnut', 'cranberry', 'lingonberry', 'goji-berry',
  'hardy-kiwi', 'kiwi', 'passion-fruit', 'mango', 'avocado',
  'mandarin', 'tangerine', 'kumquat', 'calamondin', 'citron',
  'cherimoya', 'date-palm',
];

/** Counter variants allow [a-z0-9_] only, and every id here fits once hyphens go. */
function toVariant(value: string): string {
  return value.toLowerCase().replace(/-/g, '_').slice(0, 32);
}

function reportMissingVariety(fruitType: FruitPollinationFruitType, query: string): void {
  track('planner_variety_not_found', { variant: toVariant(fruitType) });
  try {
    void fetch('/api/tools/fruit-tree-planner/missing-variety', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fruitType, query }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore.
  }
}

const STEP3_LABEL_SPACE_CLASS = 'md:min-h-[20px]';
const STEP3_HELPER_SPACE_CLASS = 'md:min-h-[2.85rem]';

function FieldHeader({
  id,
  label,
  helper,
  reserveAlignedSpace = false,
}: {
  id?: string;
  label: string;
  helper?: string;
  reserveAlignedSpace?: boolean;
}) {
  return (
    <>
      <div className={reserveAlignedSpace ? STEP3_LABEL_SPACE_CLASS : ''}>
        {id ? (
          <label htmlFor={id} className="block font-body text-[13px] font-semibold text-dark-text">
            {label}
          </label>
        ) : (
          <p className="block font-body text-[13px] font-semibold text-dark-text">{label}</p>
        )}
      </div>
      {(helper || reserveAlignedSpace) && (
        <div className={reserveAlignedSpace ? STEP3_HELPER_SPACE_CLASS : ''}>
          {helper && <p className="font-body text-[12px] text-sage">{helper}</p>}
        </div>
      )}
    </>
  );
}

function Step3ActionSlot({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="hidden md:block h-[20px]" aria-hidden="true" />
      <div className="hidden md:block min-h-[2.85rem]" aria-hidden="true" />
      {children}
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  helper,
  reserveAlignedSpace = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  helper?: string;
  reserveAlignedSpace?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <FieldHeader
        id={id}
        label={label}
        helper={helper}
        reserveAlignedSpace={reserveAlignedSpace}
      />
      <select
        id={id}
        value={value}
        onChange={event => onChange(event.target.value)}
        className="w-full rounded-xl border border-sand bg-white px-4 py-3 font-body text-[15px] text-dark-text outline-none transition-colors focus:border-sage"
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'sage' | 'gold' | 'terracotta';
  children: ReactNode;
}) {
  const toneClass = tone === 'sage'
    ? 'bg-light-sage text-deep-green border-[#C5D9B8]'
    : tone === 'gold'
      ? 'bg-[#FFF8E6] text-[#8A6A16] border-[#EBD79C]'
      : 'bg-[#FFF1EA] text-[#A04D2A] border-[#EDC1AB]';

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 font-body text-[12px] font-semibold ${toneClass}`}>
      {children}
    </span>
  );
}

function SearchableCombobox({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  helper,
  emptyState,
  resetSignal,
  searchInputPlacement = 'panel',
  reserveAlignedSpace = false,
  onNoMatch,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; searchText?: string }>;
  placeholder: string;
  helper?: string;
  emptyState?: string;
  resetSignal?: number;
  searchInputPlacement?: 'panel' | 'trigger';
  reserveAlignedSpace?: boolean;
  onNoMatch?: (query: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');
  const [useMobileModal, setUseMobileModal] = useState(false);
  const [focusDesktopSearchOnOpen, setFocusDesktopSearchOnOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerWrapperRef = useRef<HTMLDivElement | null>(null);
  const desktopInputRef = useRef<HTMLInputElement | null>(null);
  const modalInputRef = useRef<HTMLInputElement | null>(null);

  const selectedOption = useMemo(
    () => options.find(option => option.value === value) ?? null,
    [options, value],
  );

  useEffect(() => {
    setQuery(selectedOption?.label ?? '');
  }, [selectedOption?.label, value]);

  useEffect(() => {
    setQuery(selectedOption?.label ?? '');
    setIsOpen(false);
  }, [resetSignal, selectedOption?.label]);

  useEffect(() => {
    if (!isOpen) return;

    const updatePickerLayout = () => {
      const mobile = window.innerWidth < 768;
      setUseMobileModal(mobile);

      if (mobile || !triggerWrapperRef.current) {
        setOpenDirection('down');
        return;
      }

      const rect = triggerWrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const preferredHeight = 320;

      if (spaceBelow < preferredHeight && spaceAbove > spaceBelow) {
        setOpenDirection('up');
      } else {
        setOpenDirection('down');
      }
    };

    updatePickerLayout();
    window.addEventListener('resize', updatePickerLayout);
    window.addEventListener('scroll', updatePickerLayout, true);

    return () => {
      window.removeEventListener('resize', updatePickerLayout);
      window.removeEventListener('scroll', updatePickerLayout, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusDesktopSearchOnOpen(false);
        setQuery(selectedOption?.label ?? '');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen, selectedOption?.label]);

  useEffect(() => {
    if (!isOpen || useMobileModal || !focusDesktopSearchOnOpen) return;

    const frame = window.requestAnimationFrame(() => {
      desktopInputRef.current?.focus();
      const length = desktopInputRef.current?.value.length ?? 0;
      desktopInputRef.current?.setSelectionRange(length, length);
      setFocusDesktopSearchOnOpen(false);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [focusDesktopSearchOnOpen, isOpen, useMobileModal]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options;

    const startsWith: Array<{ value: string; label: string; searchText?: string }> = [];
    const contains: Array<{ value: string; label: string; searchText?: string }> = [];

    for (const option of options) {
      const searchableText = (option.searchText ?? option.label).toLowerCase();
      if (searchableText.startsWith(normalizedQuery)) {
        startsWith.push(option);
        continue;
      }
      if (searchableText.includes(normalizedQuery)) contains.push(option);
    }

    return [...startsWith, ...contains];
  }, [normalizedQuery, options]);

  const resultSummary = normalizedQuery
    ? `Showing ${filteredOptions.length} of ${options.length} options.`
    : `${options.length} options available.`;

  // Report only once the typing settles, otherwise every keystroke on the way
  // to a real variety looks like a miss.
  const hasNoMatch = Boolean(normalizedQuery) && filteredOptions.length === 0;
  useEffect(() => {
    if (!hasNoMatch || !onNoMatch) return;
    const timer = setTimeout(() => onNoMatch(normalizedQuery), NO_MATCH_REPORT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasNoMatch, normalizedQuery, onNoMatch]);

  function resetInputToSelection() {
    setQuery(selectedOption?.label ?? '');
  }

  function commitSelection(nextValue: string) {
    onChange(nextValue);
    setIsOpen(false);
    setFocusDesktopSearchOnOpen(false);
  }

  function closePicker() {
    setIsOpen(false);
    setFocusDesktopSearchOnOpen(false);
    resetInputToSelection();
  }

  function openPicker(options?: { focusSearch?: boolean; nextQuery?: string }) {
    const mobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (options?.nextQuery !== undefined) {
      setQuery(options.nextQuery);
    } else if (!isOpen) {
      resetInputToSelection();
    }

    setUseMobileModal(mobile);
    setFocusDesktopSearchOnOpen(Boolean(options?.focusSearch) && !mobile);
    setIsOpen(true);
  }

  return (
    <div ref={rootRef} className="space-y-1.5">
      <FieldHeader
        id={id}
        label={label}
        helper={helper}
        reserveAlignedSpace={reserveAlignedSpace}
      />

      <div ref={triggerWrapperRef} className="relative">
        <div className="relative">
          {searchInputPlacement === 'trigger' ? (
            <input
              id={id}
              type="text"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-controls={`${id}-options`}
              aria-autocomplete="list"
              value={query}
              placeholder={placeholder}
              autoComplete="off"
              onFocus={() => openPicker({ focusSearch: false })}
              onClick={() => openPicker({ focusSearch: false })}
              onChange={event => {
                setQuery(event.target.value);
                if (!isOpen) {
                  openPicker({ focusSearch: false, nextQuery: event.target.value });
                }
              }}
              onKeyDown={event => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  openPicker({ focusSearch: false });
                }

                if (event.key === 'Enter' && filteredOptions.length > 0) {
                  event.preventDefault();
                  commitSelection(filteredOptions[0].value);
                }

                if (event.key === 'Escape') {
                  event.preventDefault();
                  closePicker();
                }
              }}
              className="w-full rounded-xl border border-sand bg-white px-4 py-3 pr-24 font-body text-[15px] text-dark-text outline-none transition-colors placeholder:text-sage focus:border-sage"
            />
          ) : (
            <button
              id={id}
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-controls={`${id}-options`}
              onClick={() => openPicker({ focusSearch: true })}
              onKeyDown={event => {
                if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openPicker({ focusSearch: true });
                }

                if (event.key === 'Escape') {
                  event.preventDefault();
                  closePicker();
                }

                if (
                  event.key.length === 1
                  && !event.metaKey
                  && !event.ctrlKey
                  && !event.altKey
                ) {
                  event.preventDefault();
                  openPicker({ focusSearch: true, nextQuery: event.key });
                }
              }}
              className="w-full rounded-xl border border-sand bg-white px-4 py-3 pr-24 text-left font-body text-[15px] text-dark-text outline-none transition-colors focus:border-sage"
            >
              <span className={value ? 'text-dark-text' : 'text-sage'}>
                {selectedOption?.label || placeholder}
              </span>
            </button>
          )}

          <div className="absolute inset-y-0 right-3 flex items-center gap-2">
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setQuery('');
                  setIsOpen(false);
                  setFocusDesktopSearchOnOpen(false);
                }}
                className="font-body text-[12px] font-semibold text-sage transition-colors hover:text-dark-text"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              aria-label={isOpen ? 'Close options' : 'Open options'}
              onClick={() => {
                if (isOpen) {
                  closePicker();
                  return;
                }

                openPicker({ focusSearch: searchInputPlacement === 'panel' });
              }}
              className="text-sage transition-colors hover:text-dark-text"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'}
              >
                <path d="M3.5 6.25L8 10.25L12.5 6.25" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="font-body text-[12px] text-sage">{resultSummary}</p>
          {!isOpen && options.length > 8 && (
            <p className="font-body text-[12px] text-sage">Type to narrow the list.</p>
          )}
        </div>

        {isOpen && !useMobileModal && (
          <div
            id={`${id}-options`}
            role="listbox"
            className={[
              'absolute z-20 w-full overflow-hidden rounded-2xl border border-sand bg-white shadow-[0_18px_40px_rgba(47,68,43,0.14)]',
              openDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
            ].join(' ')}
          >
            <div className="border-b border-[#EFE7D5] bg-[#FAFAF9] px-3 py-3 space-y-2">
              {searchInputPlacement === 'panel' && (
                <input
                  ref={desktopInputRef}
                  type="text"
                  value={query}
                  placeholder={placeholder}
                  autoComplete="off"
                  onChange={event => setQuery(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      closePicker();
                    }
                    if (event.key === 'Enter' && filteredOptions.length > 0) {
                      event.preventDefault();
                      commitSelection(filteredOptions[0].value);
                    }
                  }}
                  className="w-full rounded-xl border border-sand bg-white px-4 py-3 font-body text-[15px] text-dark-text outline-none transition-colors focus:border-sage"
                />
              )}
              <p className="font-body text-[12px] text-sage">
                {filteredOptions.length} option{filteredOptions.length === 1 ? '' : 's'} visible. Scroll inside the list to see more.
              </p>
            </div>
            <div className="max-h-[min(20rem,50vh)] overflow-y-auto p-2">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const selected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => commitSelection(option.value)}
                      className={[
                        'flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                        selected ? 'bg-light-sage text-dark-text' : 'text-dark-text hover:bg-[#F7F4EB]',
                      ].join(' ')}
                    >
                      <span className="font-body text-[14px] leading-relaxed">{option.label}</span>
                      {selected && (
                        <span className="font-body text-[12px] font-semibold text-deep-green">
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="px-3 py-3 font-body text-[14px] text-sage">
                  {emptyState ?? 'No matching options yet.'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {isOpen && useMobileModal && (
        <div className="fixed inset-0 z-50 bg-[rgba(33,39,28,0.42)] p-0 sm:p-6">
          <div className="flex h-full w-full flex-col bg-[#FFFDF8] sm:mx-auto sm:max-w-xl sm:rounded-3xl sm:border sm:border-sand">
            <div className="flex items-center justify-between border-b border-[#EFE7D5] px-4 py-4">
              <div>
                <p className="font-heading text-[22px] text-dark-text">{label}</p>
                <p className="font-body text-[13px] text-sage mt-1">
                  {filteredOptions.length} option{filteredOptions.length === 1 ? '' : 's'} visible. Scroll inside the list to see more.
                </p>
              </div>
              <button
                type="button"
                onClick={closePicker}
                className="rounded-full border border-sand px-3 py-1.5 font-body text-[13px] font-semibold text-dark-text"
              >
                Done
              </button>
            </div>

            <div className="border-b border-[#EFE7D5] px-4 py-4">
              {searchInputPlacement === 'panel' ? (
                <input
                  ref={modalInputRef}
                  type="text"
                  value={query}
                  placeholder={placeholder}
                  autoComplete="off"
                  onChange={event => setQuery(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && filteredOptions.length > 0) {
                      event.preventDefault();
                      commitSelection(filteredOptions[0].value);
                    }
                  }}
                  className="w-full rounded-xl border border-sand bg-white px-4 py-3 font-body text-[16px] text-dark-text outline-none transition-colors focus:border-sage"
                />
              ) : (
                <p className="font-body text-[13px] text-sage">
                  Type in the fruit field above to narrow the list, then choose from the options below.
                </p>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {filteredOptions.length > 0 ? (
                <div className="space-y-2">
                  {filteredOptions.map(option => {
                    const selected = option.value === value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => commitSelection(option.value)}
                        className={[
                          'flex w-full items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors',
                          selected
                            ? 'border-deep-green bg-light-sage text-dark-text'
                            : 'border-sand bg-white text-dark-text',
                        ].join(' ')}
                      >
                        <span className="font-body text-[15px] leading-relaxed">{option.label}</span>
                        {selected && (
                          <span className="font-body text-[12px] font-semibold text-deep-green">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="px-1 py-3 font-body text-[14px] text-sage">
                  {emptyState ?? 'No matching options yet.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FruitTypePicker({
  value,
  onChange,
}: {
  value: FruitPollinationFruitType | null;
  onChange: (value: FruitPollinationFruitType | null) => void;
}) {
  const fruitTypes = listFruitPollinationTypes();

  return (
    <SearchableCombobox
      id="fruit-pollination-fruit-type"
      label="Fruit type"
      value={value ?? ''}
      onChange={nextValue => onChange(nextValue ? nextValue as FruitPollinationFruitType : null)}
      options={fruitTypes.map(fruit => ({ value: fruit.id, label: fruit.label }))}
      placeholder="Choose a fruit type"
      helper="Start here. The helper uses different rules for apples, plums, blueberries, kiwis, and other fruit groups."
      emptyState="No fruit types match that search."
      searchInputPlacement="trigger"
    />
  );
}

function ModePicker({
  value,
  onChange,
}: {
  value: PollinationMode;
  onChange: (value: PollinationMode) => void;
}) {
  const activeMode = POLLINATION_MODES.find(mode => mode.id === value) ?? POLLINATION_MODES[0];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {POLLINATION_MODES.map(mode => {
          const selected = mode.id === value;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => onChange(mode.id)}
              className={[
                'rounded-full border px-4 py-2.5 font-body text-[14px] font-semibold transition-colors',
                selected
                  ? 'border-deep-green bg-light-sage text-deep-green'
                  : 'border-sand bg-white text-dark-text hover:border-sage',
              ].join(' ')}
            >
              {mode.shortLabel}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
        <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Current goal</p>
        <p className="font-heading text-[20px] text-dark-text mt-1">{activeMode.label}</p>
        <p className="font-body text-[14px] text-sage mt-1 leading-relaxed">{activeMode.description}</p>
      </div>
    </div>
  );
}

function FlowStep({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Step {number}</p>
        <h2 className="font-heading text-[24px] text-dark-text mt-1">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FruitContextCard({
  fruitLabel,
  description,
  partnerDistanceNote,
  varietyCount,
}: {
  fruitLabel: string;
  description: string;
  partnerDistanceNote: string;
  varietyCount: number;
}) {
  return (
    <div className="rounded-xl border border-[#D7E2D0] bg-[#F6FAF3] p-4 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Selected fruit</p>
          <p className="font-heading text-[20px] text-dark-text mt-1">{fruitLabel}</p>
        </div>
        <StatusPill tone="sage">{varietyCount} tracked varieties</StatusPill>
      </div>
      <p className="font-body text-[14px] text-dark-text leading-relaxed">{description}</p>
      <p className="font-body text-[13px] text-sage leading-relaxed">{partnerDistanceNote}</p>
    </div>
  );
}

function ListBuilder({
  label,
  helper,
  currentValue,
  onCurrentValueChange,
  onAdd,
  selectedItems,
  onToggleAlreadyOwned,
  onRemove,
  options,
  disabled,
  maxSelected,
  resetSignal,
  onNoMatch,
}: {
  label: string;
  helper?: string;
  currentValue: string;
  onCurrentValueChange: (value: string) => void;
  onAdd: () => void;
  selectedItems: Array<{ id: string; label: string; alreadyOwned: boolean }>;
  onToggleAlreadyOwned: (id: string, checked: boolean) => void;
  onRemove: (id: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  maxSelected?: number;
  resetSignal?: number;
  onNoMatch?: (query: string) => void;
}) {
  const limitReached = Boolean(maxSelected && selectedItems.length >= maxSelected);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
        <SearchableCombobox
          id={`${label}-picker`}
          label={label}
          value={currentValue}
          onChange={onCurrentValueChange}
          options={disabled ? [] : options}
          placeholder="Search and choose a variety"
          helper={helper}
          emptyState={limitReached ? 'You have reached the 6-variety limit.' : 'No more matching varieties to add.'}
          resetSignal={resetSignal}
          reserveAlignedSpace
          onNoMatch={limitReached ? undefined : onNoMatch}
        />
        <Step3ActionSlot>
          <button
            type="button"
            onMouseDown={event => event.preventDefault()}
            onClick={onAdd}
            disabled={!currentValue || disabled || limitReached}
            className="h-[50px] w-full rounded-xl bg-deep-green px-5 font-body text-[14px] font-semibold text-white transition-opacity disabled:opacity-40 md:w-auto"
          >
            Add variety
          </button>
        </Step3ActionSlot>
      </div>

      {maxSelected && (
        <p className="font-body text-[12px] text-sage">
          Add between 2 and {maxSelected} varieties.
        </p>
      )}

      {selectedItems.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {selectedItems.map(item => (
            <div
              key={item.id}
              className="rounded-xl border border-sand bg-[#FAFAF9] px-3 py-3"
            >
              <p className="font-body text-[13px] text-dark-text leading-relaxed">{item.label}</p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <label className="inline-flex items-center gap-2 font-body text-[12px] text-sage">
                  <input
                    type="checkbox"
                    checked={item.alreadyOwned}
                    onChange={event => onToggleAlreadyOwned(item.id, event.target.checked)}
                    className="h-4 w-4 rounded border-sand text-deep-green focus:ring-deep-green"
                  />
                  Already have this
                </label>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className="font-body text-[12px] font-semibold text-sage transition-colors hover:text-dark-text"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Every fruit and variety the planner knows, as real links.
 *
 * The pickers only render their options once opened, so before this the page
 * named five fruits and not one of its 444 varieties. Someone looking for a
 * specific tree could not tell whether it was covered, and neither could a
 * search engine. Kept in <details> so the names are always in the document
 * without burying the tool under a wall of links.
 */
function VarietyIndex() {
  const ordered = useMemo(() => {
    const all = listFruitPollinationTypes();
    const rank = new Map(FRUIT_TYPE_DISPLAY_ORDER.map((id, index) => [id, index]));
    return [...all].sort((left, right) => {
      const leftRank = rank.get(left.id) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = rank.get(right.id) ?? Number.MAX_SAFE_INTEGER;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return left.label.localeCompare(right.label);
    });
  }, []);

  const totalVarieties = ordered.reduce((sum, type) => sum + type.varieties.length, 0);

  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
      <div>
        <h2 className="font-heading text-[24px] text-dark-text">Every fruit and variety we cover</h2>
        <p className="font-body text-[14px] text-sage mt-1 leading-relaxed">
          {totalVarieties} varieties across {ordered.length} fruit types. Open a crop to see the
          varieties, or pick one to jump straight to its pollination partners.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {ordered.map(type => (
          <details key={type.id} className="rounded-xl border border-sand bg-[#FAFAF9]">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
              <span className="font-body text-[15px] font-semibold text-dark-text">{type.label}</span>
              <span className="font-body text-[12px] text-sage">
                {type.varieties.length} {type.varieties.length === 1 ? 'variety' : 'varieties'}
              </span>
            </summary>
            <div className="border-t border-sand px-4 py-3">
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {type.varieties.map(variety => (
                  <li key={variety.id}>
                    <Link
                      href={`/tools/fruit-tree-planner?fruit=${type.id}&variety=${encodeURIComponent(variety.name)}`}
                      className="font-body text-[13px] text-deep-green hover:underline underline-offset-2"
                    >
                      {variety.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function ExplanationBlock({ explanation }: { explanation: string }) {
  if (!explanation) return null;

  return (
    <div className="rounded-xl border border-[#E7E0CF] bg-[#FFFDF8] p-4">
      <div className="space-y-3">
        {explanation.split('\n\n').map(paragraph => (
          <p key={paragraph} className="font-body text-[15px] text-dark-text leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  );
}

function BloomTimingIndicator({
  variety,
}: {
  variety: Pick<FruitPollinationVariety, 'bloomLabel' | 'bloomOrder'>;
}) {
  const timingLabel = variety.bloomOrder <= 2 ? 'Early bloom' : variety.bloomOrder <= 4 ? 'Mid bloom' : 'Late bloom';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1" aria-hidden="true">
        {[1, 2, 3, 4, 5, 6].map(position => (
          <span
            key={position}
            className={[
              'h-2.5 w-2.5 rounded-full border border-[#D7E2D0]',
              position <= variety.bloomOrder ? 'bg-deep-green' : 'bg-[#EEF4E8]',
            ].join(' ')}
          />
        ))}
      </div>
      <span className="font-body text-[13px] text-sage">
        {timingLabel} - {variety.bloomLabel}
      </span>
    </div>
  );
}

function formatSize(size: 'small' | 'medium' | 'large' | 'very large'): string {
  return size === 'very large' ? 'very large' : size;
}

function cleanVarietyInsightParagraph(text: string): string {
  let cleaned = text
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;!?])/g, '$1')
    .replace(/([,.;!?])([A-Za-z])/g, '$1 $2')
    .trim();

  cleaned = cleaned.replace(/\b([A-Za-z][A-Za-z'-]*)\s+\1\b/gi, '$1');

  const sentences = cleaned.match(/[^.!?]+[.!?]?/g) ?? [cleaned];
  const deduped: string[] = [];
  let previousNormalized = '';

  for (const sentence of sentences) {
    const trimmed = sentence.trim();
    if (!trimmed) continue;
    const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (normalized && normalized === previousNormalized) continue;
    deduped.push(trimmed);
    previousNormalized = normalized;
  }

  return deduped.join(' ').trim();
}

function getSizeRank(size: 'small' | 'medium' | 'large' | 'very large'): number {
  switch (size) {
    case 'small':
      return 1;
    case 'medium':
      return 2;
    case 'large':
      return 3;
    case 'very large':
      return 4;
  }
}

function getTimingOrder(timing: 'early' | 'early-mid' | 'mid' | 'mid-late' | 'late'): number {
  switch (timing) {
    case 'early':
      return 1;
    case 'early-mid':
      return 2;
    case 'mid':
      return 3;
    case 'mid-late':
      return 4;
    case 'late':
      return 5;
  }
}

function scoreFlavorProfile(flavorProfile: string): number {
  const text = flavorProfile.toLowerCase();
  let score = 0;
  if (/(complex|rich|intense|winey|honeyed|deep|full-flavored|premium)/.test(text)) score += 3;
  if (/(aromatic|perfume|perfumed|spice|spiced|butterscotch|lively)/.test(text)) score += 2;
  if (/(mild|clean|straightforward|light|low acidity|low tartness)/.test(text)) score -= 2;
  if (/(sweet-tart|tart-sweet|bright tart|high-acid|tart)/.test(text)) score += 1;
  if (/(very sweet|sweet)/.test(text)) score += 1;
  return score;
}

function scoreFirmness(texture: string): number {
  const text = texture.toLowerCase();
  let score = 0;
  if (/(very crisp|crisp|dense|firm|substantial|tidy|holds shape)/.test(text)) score += 3;
  if (/(tender-crisp|medium-firm|solid flesh)/.test(text)) score += 2;
  if (/(tender|soft|jammy|creamy|somewhat soft|fairly soft)/.test(text)) score -= 2;
  if (/(juicy|very juicy|dripping)/.test(text)) score -= 1;
  return score;
}

function scoreJuiciness(texture: string): number {
  const text = texture.toLowerCase();
  let score = 0;
  if (/(very juicy|juicy|jammy|soft|tender)/.test(text)) score += 2;
  if (/(dry enough|firm|dense|crisp|holds shape)/.test(text)) score -= 1;
  return score;
}

function uniqueVarieties(variety: FruitPollinationVariety, comparisonVarieties: FruitPollinationVariety[]): FruitPollinationVariety[] {
  const seen = new Set<string>();
  return [variety, ...comparisonVarieties].filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function rankPosition(values: number[], target: number): 'top' | 'bottom' | 'middle' {
  const max = Math.max(...values);
  const min = Math.min(...values);
  if (target === max && max !== min) return 'top';
  if (target === min && max !== min) return 'bottom';
  return 'middle';
}

function scoreSweetness(flavorProfile: string): number {
  const text = flavorProfile.toLowerCase();
  let score = 0;
  if (/(very sweet|honeyed|caramel|butterscotch)/.test(text)) score += 3;
  else if (/\bsweet\b/.test(text)) score += 2;
  else if (/(mildly sweet|mellow)/.test(text)) score += 1;
  if (/(low acidity|low tartness)/.test(text)) score += 1;
  if (/(sharp|high-acid|bright tart|tart|tart-sweet|sweet-tart)/.test(text)) score -= 1;
  return score;
}

function scoreTartness(flavorProfile: string): number {
  const text = flavorProfile.toLowerCase();
  let score = 0;
  if (/(sharp|high-acid|bright tart)/.test(text)) score += 3;
  else if (/(tart-sweet|sweet-tart|tart)/.test(text)) score += 2;
  else if (/(lively acidity|acidity)/.test(text)) score += 1;
  if (/(low acidity|low tartness|mellow|very sweet|honeyed)/.test(text)) score -= 1;
  return score;
}

function scoreSoftness(texture: string): number {
  return scoreJuiciness(texture) + Math.max(0, -scoreFirmness(texture));
}

function classifyHoneyberryFlavor(flavorProfile: string): 'sweeter' | 'sweet-tart' | 'more-tart' {
  const t = flavorProfile.toLowerCase();
  if (/sharp acidity|high.?acid|^tart|primarily tart/.test(t)) return 'more-tart';
  if (/sweet.?tart|tart.?sweet|tart.?to.?sweet|hint of tart|light.*tart|tart edge|acidic finish|mild.*tart/.test(t)) return 'sweet-tart';
  if (/very sweet|purely sweet|almost purely sweet/.test(t)) return 'sweeter';
  if (/\bsweet\b/.test(t) && !/\btart\b|\bacid/.test(t)) return 'sweeter';
  if (/\btart\b/.test(t)) return 'sweet-tart';
  return 'sweeter';
}

type VarietyGroupSnapshot = {
  variety: FruitPollinationVariety;
  insight: ReturnType<typeof getFruitVarietyInsight>;
  flavorIntensity: number;
  sweetness: number;
  tartness: number;
  firmness: number;
  softness: number;
  size: number;
  timing: number;
};

type VarietyGroupContext = {
  snapshots: VarietyGroupSnapshot[];
  byId: Map<string, VarietyGroupSnapshot>;
};

function buildVarietyGroupContext(groupVarieties: FruitPollinationVariety[]): VarietyGroupContext {
  const snapshots = groupVarieties.map(variety => {
    const insight = getFruitVarietyInsight(variety);
    return {
      variety,
      insight,
      flavorIntensity: scoreFlavorProfile(insight.flavorProfile),
      sweetness: scoreSweetness(insight.flavorProfile),
      tartness: scoreTartness(insight.flavorProfile),
      firmness: scoreFirmness(insight.texture),
      softness: scoreSoftness(insight.texture),
      size: getSizeRank(insight.size),
      timing: getTimingOrder(insight.harvestTiming),
    };
  });

  return {
    snapshots,
    byId: new Map(snapshots.map(snapshot => [snapshot.variety.id, snapshot])),
  };
}

function cleanFlavorDescriptor(flavorProfile: string): string {
  return flavorProfile
    .replace(/\b(?:apple|pear|plum|cherry|blueberry|berry|apricot|peach|nectarine|fig|grape|pomegranate|passion|kiwi|mango|persimmon|quince|mulberry|currant|gooseberry|jujube)\b/gi, '')
    .replace(/\bfruit\b/gi, '')
    .replace(/\bflavor\b/gi, '')
    .replace(/\bcharacter\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim()
    .replace(/^[,-]\s*/, '')
    .replace(/\s*[,-]$/, '');
}

function describeSweetnessAcidity(flavorProfile: string): string {
  const sweetness = scoreSweetness(flavorProfile);
  const tartness = scoreTartness(flavorProfile);
  const intensity = scoreFlavorProfile(flavorProfile);
  const cleaned = cleanFlavorDescriptor(flavorProfile).toLowerCase();

  if (sweetness >= 3 && tartness <= 0) return intensity >= 3 ? 'very sweet with low acidity and real depth' : 'very sweet with low acidity';
  if (tartness >= 3 && sweetness <= 1) return 'tart-forward with sharp acidity';
  if (sweetness >= 2 && tartness >= 2) return intensity >= 3 ? 'sweet up front with a tart, high-acid finish' : 'sweet with a clear tart finish';
  if (sweetness >= 2) return cleaned.includes('aromatic') || cleaned.includes('perfume') ? 'sweet with light acidity and aromatic lift' : 'sweet with just enough acidity to stay lively';
  if (tartness >= 2) return 'lightly sweet with a definite tart edge';
  if (cleaned.includes('honeyed')) return 'sweet with soft acidity and honeyed depth';
  if (cleaned.includes('spice') || cleaned.includes('spiced')) return 'sweet with a spiced edge';
  return 'sweet with a soft acidic lift';
}

function describeDetailedTexture(texture: string): string {
  const text = texture.toLowerCase();
  const firmness = scoreFirmness(texture);
  const softness = scoreSoftness(texture);

  if (text.includes('berry texture')) {
    if (text.includes('soft-juicy')) return 'soft, juicy berries';
    if (text.includes('medium-firm')) return 'medium-firm berries';
    if (text.includes('firm')) return 'firm berries';
    if (text.includes('juicy')) return 'juicy berries';
  }
  if (text.includes('buttery')) return 'soft, buttery flesh';
  if (text.includes('melting')) return 'soft, melting flesh';
  if (text.includes('crisp') && firmness >= 3) return 'very crisp, firm texture';
  if (firmness >= 3) return 'firm, dense flesh';
  if (softness >= 3) return 'soft, juicy flesh';
  if (text.includes('tender-crisp')) return 'tender-crisp flesh';
  if (text.includes('fine-grained')) return 'fine-grained, juicy flesh';
  if (text.includes('coarse')) return 'coarser, firmer flesh';
  if (text.includes('juicy')) return 'juicy flesh with moderate firmness';
  return 'medium-firm flesh';
}

function normalizeBestUses(bestUses: string): string {
  return bestUses
    .replace(/\bgrowers who want\b/gi, '')
    .replace(/\bpeople who want\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();
}

function normalizeTradeoff(tradeoff: string): string {
  return tradeoff
    .replace(/\bgrowers who want\b/gi, '')
    .replace(/\bpeople who want\b/gi, '')
    .replace(/\bgrowers who need\b/gi, '')
    .replace(/\bpeople who need\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();
}

function buildFlavorTextureSentence(snapshot: VarietyGroupSnapshot): string {
  return `${snapshot.variety.name} is ${describeSweetnessAcidity(snapshot.insight.flavorProfile)}, with ${describeDetailedTexture(snapshot.insight.texture)} and ${formatSize(snapshot.insight.size)} fruit.`;
}

function buildGroupRoleSentence(
  snapshot: VarietyGroupSnapshot,
  context: VarietyGroupContext,
): string {
  const flavorRank = rankPosition(context.snapshots.map(item => item.flavorIntensity), snapshot.flavorIntensity);
  const sweetRank = rankPosition(context.snapshots.map(item => item.sweetness), snapshot.sweetness);
  const tartRank = rankPosition(context.snapshots.map(item => item.tartness), snapshot.tartness);
  const firmnessRank = rankPosition(context.snapshots.map(item => item.firmness), snapshot.firmness);
  const softnessRank = rankPosition(context.snapshots.map(item => item.softness), snapshot.softness);
  const sizeRank = rankPosition(context.snapshots.map(item => item.size), snapshot.size);
  const timingRank = rankPosition(context.snapshots.map(item => item.timing), snapshot.timing);

  if (flavorRank === 'top' && sweetRank === 'top') {
    return 'In this group, this is one of the sweetest and richest picks.';
  }

  if (flavorRank === 'top' && tartRank !== 'top') {
    return 'In this group, this is one of the richest-tasting options.';
  }

  if (tartRank === 'top') {
    return 'In this group, this brings more acidity and bite than most of the others shown.';
  }

  if (firmnessRank === 'top') {
    return 'In this group, it is one of the firmest choices, so it handles baking, slicing, or holding its shape better.';
  }

  if (softnessRank === 'top') {
    return 'In this group, it is one of the softer, juicier choices, so it leans more toward fresh eating than long holding.';
  }

  if (sizeRank === 'top') {
    return 'In this group, it gives some of the largest fruit and feels more substantial than most of the others shown.';
  }

  if (sweetRank === 'top') {
    return 'In this group, it is one of the sweeter, lower-acid choices.';
  }

  if (timingRank === 'bottom') {
    return 'In this group, it is one of the earliest ripening choices.';
  }

  if (timingRank === 'top') {
    return 'In this group, it is one of the latest ripening choices.';
  }

  return '';
}

function buildUseTradeoffSentence(snapshot: VarietyGroupSnapshot): string {
  return `Choose it for ${normalizeBestUses(snapshot.insight.bestUses)}, but skip it if you want ${normalizeTradeoff(snapshot.insight.tradeoff)}.`;
}

function buildHarvestGroupSentence(
  snapshot: VarietyGroupSnapshot,
  context: VarietyGroupContext,
  zone: number | null,
): string {
  const timingRank = rankPosition(context.snapshots.map(item => item.timing), snapshot.timing);
  const harvestWindow = getFruitVarietyHarvestWindow(snapshot.variety, zone);

  const relativeLabel = timingRank === 'bottom'
    ? 'earlier than most of the others shown'
    : timingRank === 'top'
      ? 'later than most of the others shown'
      : 'in the middle of this group';

  return zone
    ? `In zone ${zone}, it usually ripens around ${harvestWindow}, ${relativeLabel}.`
    : `It usually ripens around ${harvestWindow}, ${relativeLabel}.`;
}

function buildSelectedVarietyDescription({
  variety,
  zone,
}: {
  variety: FruitPollinationVariety;
  zone?: number | null;
}): string {
  const insight = getFruitVarietyInsight(variety);
  const snapshot: VarietyGroupSnapshot = {
    variety,
    insight,
    flavorIntensity: scoreFlavorProfile(insight.flavorProfile),
    sweetness: scoreSweetness(insight.flavorProfile),
    tartness: scoreTartness(insight.flavorProfile),
    firmness: scoreFirmness(insight.texture),
    softness: scoreSoftness(insight.texture),
    size: getSizeRank(insight.size),
    timing: getTimingOrder(insight.harvestTiming),
  };
  const harvestWindow = getFruitVarietyHarvestWindow(variety, zone ?? null);
  const harvestLine = zone
    ? `In zone ${zone}, it usually ripens around ${harvestWindow}.`
    : `It usually ripens around ${harvestWindow}.`;
  return cleanVarietyInsightParagraph([
    buildFlavorTextureSentence(snapshot),
    buildUseTradeoffSentence(snapshot),
    harvestLine,
  ].join(' '));
}

function buildVarietyInsightParagraph({
  variety,
  comparisonVarieties = [],
  zone,
}: {
  variety: FruitPollinationVariety;
  comparisonVarieties?: FruitPollinationVariety[];
  zone?: number | null;
}) {
  const groupVarieties = uniqueVarieties(variety, comparisonVarieties);
  const context = buildVarietyGroupContext(groupVarieties);
  const snapshot = context.byId.get(variety.id);
  if (!snapshot) return '';

  const paragraph = [
    buildFlavorTextureSentence(snapshot),
    buildGroupRoleSentence(snapshot, context),
    buildUseTradeoffSentence(snapshot),
    buildHarvestGroupSentence(snapshot, context, zone ?? null),
  ].join(' ');

  return cleanVarietyInsightParagraph(paragraph);
}

function VarietyInsightDetails({
  variety,
  comparisonVarieties,
  zone,
}: {
  variety: FruitPollinationVariety;
  comparisonVarieties?: FruitPollinationVariety[];
  zone?: number | null;
}) {
  return (
    <details className="group rounded-xl border border-[#E7E0CF] bg-[#FFFDF8]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <span className="font-body text-[13px] font-semibold text-deep-green">Why choose this?</span>
        <span className="text-sage">
          <ChevronSmallDownIcon />
        </span>
      </summary>
      <div className="border-t border-[#EFE7D5] px-4 py-3">
        <p className="font-body text-[14px] text-dark-text leading-relaxed">
          {buildVarietyInsightParagraph({ variety, comparisonVarieties, zone })}
        </p>
      </div>
    </details>
  );
}

function ChevronSmallDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-transform group-open:rotate-180"
    >
      <path d="M3.5 6.25L8 10.25L12.5 6.25" />
    </svg>
  );
}

function MatchCard({
  match,
  comparisonVarieties,
  zone,
}: {
  match: PollinationPartnerMatch;
  comparisonVarieties: FruitPollinationVariety[];
  zone?: number | null;
}) {
  const tone = match.rank === 'best' ? 'sage' : match.rank === 'good' ? 'gold' : 'terracotta';
  return (
    <article className="rounded-xl border border-sand bg-white p-4 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-heading text-[19px] text-dark-text">{match.variety.name}</h4>
          <p className="font-body text-[13px] text-sage mt-0.5">
            {match.variety.speciesGroupLabel ?? 'Same fruit type'}
          </p>
          <div className="mt-2">
            <BloomTimingIndicator variety={match.variety} />
          </div>
        </div>
        <StatusPill tone={tone}>
          {match.rank === 'best' ? 'Best match' : match.rank === 'good' ? 'Good backup' : match.rank === 'okay' ? 'Less ideal' : 'Avoid'}
        </StatusPill>
      </div>

      <p className="font-body text-[14px] text-dark-text leading-relaxed">{match.reason}</p>

      <div className="flex flex-wrap gap-2">
        <StatusPill tone={match.zoneFit === 'outside' ? 'terracotta' : 'sage'}>
          {match.zoneFit === 'good' ? 'Good zone fit' : match.zoneFit === 'outside' ? 'Zone caution' : 'Zone unknown'}
        </StatusPill>
        <StatusPill tone={match.bloomFit === 'same' ? 'sage' : match.bloomFit === 'nearby' ? 'gold' : 'terracotta'}>
          {match.bloomFit === 'same' ? 'Same bloom window' : match.bloomFit === 'nearby' ? 'Nearby bloom window' : 'Weak bloom overlap'}
        </StatusPill>
      </div>

      {match.warnings.length > 0 && (
        <div className="space-y-1">
          {match.warnings.map(warning => (
            <p key={warning} className="font-body text-[13px] text-sage leading-relaxed">
              {warning}
            </p>
          ))}
        </div>
      )}

      <VarietyInsightDetails
        variety={match.variety}
        comparisonVarieties={comparisonVarieties}
        zone={zone}
      />
    </article>
  );
}

function MatchSection({
  title,
  description,
  matches,
  zone,
}: {
  title: string;
  description: string;
  matches: PollinationPartnerMatch[];
  zone?: number | null;
}) {
  if (matches.length === 0) return null;

  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-heading text-[22px] text-dark-text">{title}</h3>
        <p className="font-body text-[14px] text-sage mt-1">{description}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {matches.map(match => (
          <MatchCard
            key={match.variety.id}
            match={match}
            comparisonVarieties={matches.map(item => item.variety)}
            zone={zone}
          />
        ))}
      </div>
    </section>
  );
}

function PlantingGuidanceCard({
  minimumSetup,
  idealSetup,
  fallbackPlan,
}: {
  minimumSetup: string;
  idealSetup: string;
  fallbackPlan: string;
}) {
  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
      <div>
        <h2 className="font-heading text-[24px] text-dark-text">Minimum planting guidance</h2>
        <p className="font-body text-[14px] text-sage mt-1">
          Use the minimum, ideal, and fallback plan to size the planting to your space and goals.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Minimum setup</p>
          <p className="font-body text-[14px] text-dark-text mt-2 leading-relaxed">{minimumSetup}</p>
        </div>
        <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Ideal setup</p>
          <p className="font-body text-[14px] text-dark-text mt-2 leading-relaxed">{idealSetup}</p>
        </div>
        <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Fallback plan</p>
          <p className="font-body text-[14px] text-dark-text mt-2 leading-relaxed">{fallbackPlan}</p>
        </div>
      </div>
    </section>
  );
}

function PairSummarySection({
  title,
  pairs,
}: {
  title: string;
  pairs: StoreModePairing[];
}) {
  if (pairs.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
      <h2 className="font-heading text-[24px] text-dark-text">{title}</h2>
      <div className="space-y-3">
        {pairs.map(pair => (
          <article
            key={`${pair.firstVariety.id}-${pair.secondVariety.id}`}
            className="rounded-xl border border-sand bg-[#FAFAF9] p-4"
          >
            <p className="font-heading text-[18px] text-dark-text">
              {pair.firstVariety.name} + {pair.secondVariety.name}
            </p>
            <p className="font-body text-[14px] text-dark-text mt-2 leading-relaxed">{pair.summary}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdditionSection({
  suggestions,
  zone,
}: {
  suggestions: PollinationAdditionSuggestion[];
  zone?: number | null;
}) {
  if (suggestions.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
      <div>
        <h2 className="font-heading text-[24px] text-dark-text">Best additions</h2>
        <p className="font-body text-[14px] text-sage mt-1">
          These are the strongest next plants to add if you want to improve coverage instead of starting over.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {suggestions.map(item => (
          <article key={item.variety.id} className="rounded-xl border border-sand bg-[#FAFAF9] p-4 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="font-heading text-[19px] text-dark-text">{item.variety.name}</h3>
                <p className="font-body text-[13px] text-sage mt-0.5">
                  Helps {item.coverageCount} {item.coverageCount === 1 ? 'plant' : 'plants'}
                </p>
              </div>
              <StatusPill tone={item.rank === 'best' ? 'sage' : 'gold'}>
                {item.rank === 'best' ? 'Best addition' : item.rank === 'good' ? 'Good add' : 'Possible add'}
              </StatusPill>
            </div>
            <p className="font-body text-[14px] text-dark-text leading-relaxed">{item.reason}</p>
            <VarietyInsightDetails
              variety={item.variety}
              comparisonVarieties={suggestions.map(suggestion => suggestion.variety)}
              zone={zone}
            />
          </article>
        ))}
      </div>
    </section>
  );
}

function ExistingVarietyPartnerCards({
  existingVariety,
  pairs,
  zone,
}: {
  existingVariety: FruitPollinationVariety;
  pairs: StoreModePairing[];
  zone?: number | null;
}) {
  if (pairs.length === 0) return null;

  const partnerVarieties = pairs.map(pair => (
    pair.firstVariety.id === existingVariety.id ? pair.secondVariety : pair.firstVariety
  ));

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {pairs.map(pair => {
        const partner = pair.firstVariety.id === existingVariety.id
          ? pair.secondVariety
          : pair.firstVariety;

        return (
          <article key={`${existingVariety.id}-${partner.id}`} className="rounded-xl border border-sand bg-white p-4 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h4 className="font-heading text-[19px] text-dark-text">{partner.name}</h4>
                <p className="font-body text-[13px] text-sage mt-0.5">
                  {partner.speciesGroupLabel ?? 'Same fruit type'}
                </p>
              </div>
              <StatusPill tone={pair.rank === 'best' ? 'sage' : pair.rank === 'good' ? 'gold' : 'terracotta'}>
                {pair.rank === 'best' ? 'Best partner' : pair.rank === 'good' ? 'Good backup' : pair.rank === 'okay' ? 'Less ideal' : 'Avoid'}
              </StatusPill>
            </div>

            <div>
              <BloomTimingIndicator variety={partner} />
            </div>

            <p className="font-body text-[14px] text-dark-text leading-relaxed">{pair.summary}</p>

            <VarietyInsightDetails
              variety={partner}
              comparisonVarieties={partnerVarieties}
              zone={zone}
            />
          </article>
        );
      })}
    </div>
  );
}

function NotesSection({
  warnings,
  notes,
}: {
  warnings: string[];
  notes: string[];
}) {
  if (warnings.length === 0 && notes.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
      <div>
        <h2 className="font-heading text-[24px] text-dark-text">Important notes</h2>
        <p className="font-body text-[14px] text-sage mt-1">
          These notes keep the key cautions and practical details easy to scan.
        </p>
      </div>

      {warnings.length > 0 && (
        <div className="space-y-2">
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Warnings</p>
          {warnings.map(warning => (
            <p key={warning} className="font-body text-[14px] text-dark-text leading-relaxed">
              {warning}
            </p>
          ))}
        </div>
      )}

      {notes.length > 0 && (
        <div className="space-y-2">
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Helpful notes</p>
          {notes.map(note => (
            <p key={note} className="font-body text-[14px] text-dark-text leading-relaxed">
              {note}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

function Step4ArticleLinks() {
  return (
    <section className="pt-2 md:pt-4">
      <div className="space-y-2">
        <Link
          href="/learn/articles/fruit-tree-pollination"
          className="inline-flex text-[14px] text-sage transition-colors hover:text-deep-green"
        >
          Want to understand why these pairings work? Read the full pollination guide →
        </Link>
        <div>
          <Link
            href="/learn/articles/fruit-tree-husbandry"
            className="inline-flex text-[13px] text-sage/90 transition-colors hover:text-deep-green"
          >
            New to fruit trees? Start with fruit tree husbandry →
          </Link>
        </div>
      </div>
    </section>
  );
}

function FilterPillRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-start gap-2">
      <span className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage w-[76px] shrink-0 pt-1">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`px-3 py-1 rounded-full border text-[12px] font-body transition-colors ${
            value === null
              ? 'bg-deep-green text-white border-deep-green'
              : 'bg-white text-dark-text border-sand hover:border-sage'
          }`}
        >
          Any
        </button>
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(value === opt.value ? null : opt.value)}
            className={`px-3 py-1 rounded-full border text-[12px] font-body transition-colors ${
              value === opt.value
                ? 'bg-deep-green text-white border-deep-green'
                : 'bg-white text-dark-text border-sand hover:border-sage'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function HoneyberryFilterBar({
  sizeFilter, onSizeChange,
  flavorFilter, onFlavorChange,
  bloomFilter, onBloomChange,
  harvestFilter, onHarvestChange,
  totalCount,
  filteredCount,
}: {
  sizeFilter: string | null;
  onSizeChange: (v: string | null) => void;
  flavorFilter: string | null;
  onFlavorChange: (v: string | null) => void;
  bloomFilter: string | null;
  onBloomChange: (v: string | null) => void;
  harvestFilter: string | null;
  onHarvestChange: (v: string | null) => void;
  totalCount: number;
  filteredCount: number;
}) {
  const isFiltered = Boolean(sizeFilter || flavorFilter || bloomFilter || harvestFilter);
  return (
    <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-body text-[13px] font-semibold text-dark-text">Filter varieties</p>
        {isFiltered && (
          <p className="font-body text-[12px] text-sage">
            Showing {filteredCount} of {totalCount}
          </p>
        )}
      </div>
      <FilterPillRow
        label="Size"
        options={[
          { value: 'small-medium', label: 'Small or medium' },
          { value: 'large', label: 'Large' },
          { value: 'very-large', label: 'Very large' },
        ]}
        value={sizeFilter}
        onChange={onSizeChange}
      />
      <FilterPillRow
        label="Flavor"
        options={[
          { value: 'sweeter', label: 'Sweeter' },
          { value: 'sweet-tart', label: 'Sweet-tart' },
          { value: 'more-tart', label: 'More tart' },
        ]}
        value={flavorFilter}
        onChange={onFlavorChange}
      />
      <FilterPillRow
        label="Bloom time"
        options={[
          { value: 'very-early', label: 'Very early' },
          { value: 'early', label: 'Early' },
          { value: 'mid', label: 'Mid' },
          { value: 'late', label: 'Late' },
          { value: 'very-late', label: 'Very late' },
        ]}
        value={bloomFilter}
        onChange={onBloomChange}
      />
      <FilterPillRow
        label="Harvest"
        options={[
          { value: 'early', label: 'Early' },
          { value: 'mid', label: 'Mid' },
          { value: 'late', label: 'Late' },
        ]}
        value={harvestFilter}
        onChange={onHarvestChange}
      />
    </div>
  );
}

function LockedResultsCard() {
  useEffect(() => {
    track('upgrade_prompt_shown', { variant: 'fruit_tree_compare' });
  }, []);

  return (
    <section className="bg-white rounded-xl border border-[#D4E6CC] p-8 text-center space-y-3">
      <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">
        Comparison Results
      </p>
      <h2 className="font-heading text-[22px] text-dark-text">
        Unlock head-to-head comparison
      </h2>
      <p className="font-body text-[14px] text-sage leading-relaxed max-w-sm mx-auto">
        See ranked pairings across your whole list, partner suggestions for trees you already have, and what to add next, with Garden Pro. Looking up a single variety stays free.
      </p>
      <Link
        href="/pricing?source=prompt"
        onClick={() => track('upgrade_prompt_clicked', { variant: 'fruit_tree_compare' })}
        className="inline-block bg-deep-green text-white font-body font-bold text-[14px] px-6 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
      >
        Upgrade to Pro
      </Link>
    </section>
  );
}

function SingleModeResults({
  result,
  explanation,
  zone,
}: {
  result: FruitPollinationResult;
  explanation: string;
  zone?: number | null;
}) {
  return (
    <>
      <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Selected variety</p>
            <h2 className="font-heading text-[28px] text-dark-text mt-1">{result.selectedVariety.name}</h2>
            <p className="font-body text-[14px] text-sage mt-1">
              {result.selectedVariety.speciesGroupLabel ?? result.fruitType.label}
            </p>
            <div className="mt-2">
              <BloomTimingIndicator variety={result.selectedVariety} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusPill tone={result.selectedVariety.selfFertility === 'self-fertile' ? 'sage' : 'gold'}>
              {result.selfFertilityLabel}
            </StatusPill>
            <StatusPill tone="sage">Partner guidance included</StatusPill>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
            <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Bloom timing</p>
            <p className="font-body text-[15px] text-dark-text mt-2 leading-relaxed">{result.bloomSummary}</p>
          </div>
          <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
            <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">What to do</p>
            <p className="font-body text-[15px] text-dark-text mt-2 leading-relaxed">{result.crossPollinationReason}</p>
          </div>
        </div>

        {(() => {
          const fruitDescription = buildSelectedVarietyDescription({ variety: result.selectedVariety, zone });
          return fruitDescription ? (
            <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
              <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">About this variety</p>
              <p className="font-body text-[15px] text-dark-text mt-2 leading-relaxed">{fruitDescription}</p>
            </div>
          ) : null;
        })()}

        <p className="font-body text-[14px] text-sage leading-relaxed">
          This shows compatible pollinators. Choose based on flavor and harvest timing.
        </p>

        {result.zoneSummary && (
          <div className="rounded-xl border border-[#D7E2D0] bg-[#F6FAF3] p-4">
            <p className="font-body text-[14px] text-dark-text leading-relaxed">{result.zoneSummary}</p>
          </div>
        )}

        <ExplanationBlock explanation={explanation} />
      </section>

      {result.pairCheck && (
        <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Pair check</p>
              <h2 className="font-heading text-[24px] text-dark-text mt-1">
                {result.selectedVariety.name} + {result.secondVariety?.name}
              </h2>
            </div>
            <StatusPill tone={result.pairCheck.status === 'good' ? 'sage' : result.pairCheck.status === 'limited' ? 'gold' : 'terracotta'}>
              {result.pairCheck.status === 'good' ? 'Good match' : result.pairCheck.status === 'limited' ? 'Limited match' : 'Avoid'}
            </StatusPill>
          </div>
          <p className="font-body text-[16px] text-dark-text leading-relaxed">{result.pairCheck.summary}</p>
          <div className="space-y-2">
            {result.pairCheck.details.map(detail => (
              <p key={detail} className="font-body text-[14px] text-dark-text leading-relaxed">
                {detail}
              </p>
            ))}
          </div>
        </section>
      )}

      <MatchSection
        title="Best matches"
        description="These are the strongest practical partners based on bloom overlap, compatibility, and overall fit."
        matches={result.bestMatches}
        zone={zone}
      />
      <MatchSection
        title="Good backups"
        description="These are still useful choices when the very best option is not available at your nursery."
        matches={result.goodBackups}
        zone={zone}
      />
      <MatchSection
        title="Compatible but less ideal"
        description="These can still help, but they have more tradeoffs around timing, fit, or pollination strength."
        matches={result.lessIdealMatches}
        zone={zone}
      />
      <MatchSection
        title="Avoid"
        description="These are poor bets to rely on for this variety."
        matches={result.avoidMatches}
        zone={zone}
      />
      <PlantingGuidanceCard {...result.plantingGuidance} />
      <NotesSection warnings={result.importantWarnings} notes={result.plainLanguageNotes} />
    </>
  );
}

function CompareModeResults({
  result,
  explanation,
  zone,
  isPro,
}: {
  result: CompareVarietiesResult;
  explanation: string;
  zone?: number | null;
  isPro: boolean;
}) {
  return (
    <>
      <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
        <div>
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Compared varieties</p>
          <h2 className="font-heading text-[28px] text-dark-text mt-1">
            {result.selectedVarieties.length} {result.selectedVarieties.length === 1 ? 'variety' : 'varieties'} under review
          </h2>
          <p className="font-body text-[14px] text-sage mt-1">
            {result.selectedVarieties.map(variety => variety.name).join(', ')}
          </p>
          {result.usesExistingVarietyFocus && (
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill tone="sage">
                Already have: {result.existingVarieties.map(variety => variety.name).join(', ')}
              </StatusPill>
              {result.candidateVarieties.length > 0 && (
                <StatusPill tone="gold">
                  Considering: {result.candidateVarieties.map(variety => variety.name).join(', ')}
                </StatusPill>
              )}
            </div>
          )}
        </div>
        {isPro && (
          <>
            <div className="rounded-xl border border-[#D7E2D0] bg-[#F6FAF3] p-4">
              <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Verdict</p>
              <p className="font-body text-[15px] text-dark-text mt-2 leading-relaxed">{result.verdict}</p>
            </div>
            {result.simpleRecommendation && (
              <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
                <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Simple recommendation</p>
                <p className="font-body text-[15px] text-dark-text mt-2 leading-relaxed">{result.simpleRecommendation}</p>
              </div>
            )}
            <p className="font-body text-[14px] text-sage leading-relaxed">
              This shows compatible pollinators. Choose based on flavor and harvest timing.
            </p>
            <ExplanationBlock explanation={explanation} />
          </>
        )}
      </section>

      {isPro ? (
        <>
          {result.usesExistingVarietyFocus && result.existingVarietySummaries.length > 0 && (
            <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
              <h2 className="font-heading text-[24px] text-dark-text">Best partners for what you already have</h2>
              <div className="space-y-4">
                {result.existingVarietySummaries.map(summary => (
                  <article key={summary.existingVariety.id} className="rounded-xl border border-sand bg-[#FAFAF9] p-4 space-y-3">
                    <div>
                      <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Existing variety</p>
                      <h3 className="font-heading text-[22px] text-dark-text mt-1">{summary.existingVariety.name}</h3>
                    </div>
                    {summary.bestPartners.length > 0 ? (
                      <div className="space-y-2">
                        <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Best partners from your list</p>
                        <ExistingVarietyPartnerCards
                          existingVariety={summary.existingVariety}
                          pairs={summary.bestPartners}
                          zone={zone}
                        />
                      </div>
                    ) : summary.missingMessage ? (
                      <p className="font-body text-[14px] text-dark-text leading-relaxed">{summary.missingMessage}</p>
                    ) : null}
                    {summary.weakPartners.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Weak or incompatible options</p>
                        {summary.weakPartners.map(pair => {
                          const partnerName = pair.firstVariety.id === summary.existingVariety.id
                            ? pair.secondVariety.name
                            : pair.firstVariety.name;
                          return (
                            <p key={`${summary.existingVariety.id}-${partnerName}-weak`} className="font-body text-[14px] text-dark-text leading-relaxed">
                              {partnerName}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {result.bestPairing && (
            <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Best pair from your list</p>
                  <h2 className="font-heading text-[24px] text-dark-text mt-1">
                    {result.bestPairing.firstVariety.name} + {result.bestPairing.secondVariety.name}
                  </h2>
                </div>
                <StatusPill tone={result.bestPairing.rank === 'best' ? 'sage' : 'gold'}>
                  {result.bestPairing.rank === 'best' ? 'Best pair' : 'Workable pair'}
                </StatusPill>
              </div>
              <p className="font-body text-[15px] text-dark-text leading-relaxed">{result.bestPairing.summary}</p>
            </section>
          )}

          <PairSummarySection title="Other workable pairings" pairs={result.otherWorkablePairings} />
          {result.generalAlternativePairings.length > 0 && (
            <PairSummarySection title="General alternative pairings" pairs={result.generalAlternativePairings} />
          )}

          {(result.weakCombinations.length > 0 || result.missingCoverage.length > 0 || result.frostRiskNote) && (
            <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
              <h2 className="font-heading text-[24px] text-dark-text">Weak spots</h2>
              {result.weakCombinations.length > 0 && (
                <div className="space-y-3">
                  {result.weakCombinations.map(pair => (
                    <article key={`${pair.firstVariety.id}-${pair.secondVariety.id}`} className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
                      <p className="font-heading text-[18px] text-dark-text">
                        {pair.firstVariety.name} + {pair.secondVariety.name}
                      </p>
                      <p className="font-body text-[14px] text-dark-text mt-2 leading-relaxed">{pair.summary}</p>
                    </article>
                  ))}
                </div>
              )}
              {result.missingCoverage.map(item => (
                <p key={item} className="font-body text-[14px] text-dark-text leading-relaxed">
                  {item}
                </p>
              ))}
              {result.frostRiskNote && (
                <p className="font-body text-[14px] text-dark-text leading-relaxed">{result.frostRiskNote}</p>
              )}
            </section>
          )}

          {!result.usesExistingVarietyFocus && <AdditionSection suggestions={result.bestAdditions} zone={zone} />}
          <PlantingGuidanceCard {...result.plantingGuidance} />
          <NotesSection warnings={result.importantWarnings} notes={[]} />
        </>
      ) : (
        <LockedResultsCard />
      )}
    </>
  );
}

export default function FruitTreePollinationHelperContent({
  initialFruitType = null,
  initialVarietyQuery = null,
}: {
  initialFruitType?: FruitPollinationFruitType | null;
  initialVarietyQuery?: string | null;
}) {
  const { user } = useAuth();
  const isPro = user ? isProUser(user) : false;
  const { zone: savedZone } = useUserData();
  const [hasMounted, setHasMounted] = useState(false);
  const [mode, setMode] = useState<PollinationMode>('single');
  const [fruitType, setFruitType] = useState<FruitPollinationFruitType | null>(initialFruitType);
  // Resolved during render rather than in an effect, so a ?variety= link arrives
  // with its results already in the HTML instead of after hydration.
  const [selectedVarietyId, setSelectedVarietyId] = useState(() => (
    initialFruitType && initialVarietyQuery
      ? resolveFruitPollinationVarietyByQuery(initialFruitType, initialVarietyQuery)?.id ?? ''
      : ''
  ));
  const [secondVarietyId, setSecondVarietyId] = useState('');
  const [listDraftId, setListDraftId] = useState('');
  const [selectedVarietyIds, setSelectedVarietyIds] = useState<string[]>([]);
  const [existingVarietyIds, setExistingVarietyIds] = useState<string[]>([]);
  const [zoneValue, setZoneValue] = useState('');
  const [comparePickerResetSignal, setComparePickerResetSignal] = useState(0);
  const [hbSizeFilter, setHbSizeFilter] = useState<string | null>(null);
  const [hbFlavorFilter, setHbFlavorFilter] = useState<string | null>(null);
  const [hbBloomFilter, setHbBloomFilter] = useState<string | null>(null);
  const [hbHarvestFilter, setHbHarvestFilter] = useState<string | null>(null);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (hasMounted && !zoneValue && savedZone) setZoneValue(String(savedZone));
  }, [hasMounted, savedZone, zoneValue]);

  // The state initialisers above only run on the first mount, so a link from
  // the variety index, which is a client-side navigation to the same route,
  // would change the URL and leave the pickers showing the previous selection.
  useEffect(() => {
    if (!initialFruitType) return;
    setFruitType(initialFruitType);
    const linked = initialVarietyQuery
      ? resolveFruitPollinationVarietyByQuery(initialFruitType, initialVarietyQuery)
      : null;
    setSelectedVarietyId(linked?.id ?? '');
    setSecondVarietyId('');
  }, [initialFruitType, initialVarietyQuery]);

  const fruit = useMemo(
    () => (fruitType ? getFruitPollinationType(fruitType) : null),
    [fruitType],
  );
  const currentZone = zoneValue ? parseInt(zoneValue, 10) : null;
  const zoneHelperText = hasMounted && savedZone
    ? `Your saved zone is ${savedZone}.`
    : 'Used to sort matches and flag climate cautions.';

  const varietyOptions = useMemo(
    () => (fruit
      ? fruit.varieties
        .map(variety => ({
          value: variety.id,
          label: `${variety.name}${variety.speciesGroupLabel ? ` - ${variety.speciesGroupLabel}` : ''}`,
          searchText: [
            variety.name,
            ...(variety.aliases ?? []),
            variety.speciesGroupLabel ?? '',
          ].join(' '),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      : []),
    [fruit],
  );

  const filteredVarietyOptions = useMemo(() => {
    if (fruitType !== 'honeyberry' || (!hbSizeFilter && !hbFlavorFilter && !hbBloomFilter && !hbHarvestFilter)) {
      return varietyOptions;
    }
    return varietyOptions.filter(option => {
      const variety = fruit?.varieties.find(v => v.id === option.value);
      if (!variety) return true;
      const insight = getFruitVarietyInsight(variety);
      if (!insight) return true;
      if (hbSizeFilter) {
        if (hbSizeFilter === 'small-medium' && insight.size !== 'small' && insight.size !== 'medium') return false;
        if (hbSizeFilter === 'large' && insight.size !== 'large') return false;
        if (hbSizeFilter === 'very-large' && insight.size !== 'very large') return false;
      }
      if (hbFlavorFilter && classifyHoneyberryFlavor(insight.flavorProfile) !== hbFlavorFilter) return false;
      if (hbBloomFilter && variety.bloomGroup !== hbBloomFilter) return false;
      if (hbHarvestFilter && insight.harvestTiming !== hbHarvestFilter) return false;
      return true;
    });
  }, [fruit, fruitType, hbSizeFilter, hbFlavorFilter, hbBloomFilter, hbHarvestFilter, varietyOptions]);

  const secondVarietyOptions = useMemo(
    () => filteredVarietyOptions.filter(option => option.value !== selectedVarietyId),
    [selectedVarietyId, filteredVarietyOptions],
  );

  const listOptions = useMemo(
    () => filteredVarietyOptions.filter(option => !selectedVarietyIds.includes(option.value)),
    [selectedVarietyIds, filteredVarietyOptions],
  );

  const selectedItems = useMemo(
    () => selectedVarietyIds.flatMap(id => {
      const item = varietyOptions.find(option => option.value === id);
      return item
        ? [{ id: item.value, label: item.label, alreadyOwned: existingVarietyIds.includes(item.value) }]
        : [];
    }),
    [existingVarietyIds, selectedVarietyIds, varietyOptions],
  );

  const singleResult = useMemo(
    () => (
      fruitType && selectedVarietyId
        ? buildFruitPollinationResult(fruitType, selectedVarietyId, currentZone, secondVarietyId || null)
        : null
    ),
    [currentZone, fruitType, secondVarietyId, selectedVarietyId],
  );

  const compareResult = useMemo(
    () => (
      fruitType && selectedVarietyIds.length >= 2
        ? buildCompareVarietiesResult(fruitType, selectedVarietyIds, currentZone, existingVarietyIds)
        : null
    ),
    [currentZone, existingVarietyIds, fruitType, selectedVarietyIds],
  );

  // Written locally from data already on the result, so there is nothing to
  // fetch and nothing to wait for.
  const explanation = useMemo(() => {
    if (mode === 'single' && singleResult) return buildFruitPollinationFallbackExplanation(singleResult);
    if (mode === 'compare' && isPro && compareResult) return buildCompareVarietiesFallbackExplanation(compareResult);
    return '';
  }, [compareResult, isPro, mode, singleResult]);


  function clearSelections() {
    setSelectedVarietyId('');
    setSecondVarietyId('');
    setListDraftId('');
    setSelectedVarietyIds([]);
    setExistingVarietyIds([]);
    setComparePickerResetSignal(current => current + 1);
    setHbSizeFilter(null);
    setHbFlavorFilter(null);
    setHbBloomFilter(null);
    setHbHarvestFilter(null);
  }

  function handleFruitTypeChange(next: FruitPollinationFruitType | null) {
    if (next) track('planner_fruit_selected', { variant: toVariant(next) });
    clearSelections();
    setFruitType(next);
  }

  function handleModeChange(next: PollinationMode) {
    if (next === 'compare') {
      track('planner_compare_opened', { variant: isPro ? 'pro' : 'free' });
    }
    clearSelections();
    setMode(next);
  }

  function handleSelectedVarietyChange(next: string) {
    if (next) track('planner_variety_selected', { variant: toVariant(next) });
    setSelectedVarietyId(next);
  }

  function handleZoneChange(next: string) {
    if (next) track('planner_zone_set', { variant: `zone_${next}` });
    setZoneValue(next);
  }

  // A picker can show no matches for a variety we do carry: the second-variety
  // box hides the main selection, the compare box hides what is already added,
  // and the honeyberry filters hide the rest. Only report text that matches
  // nothing in the crop at all.
  const handleVarietyNotFound = useCallback((query: string) => {
    if (!fruitType) return;
    if (resolveFruitPollinationVarietyByQuery(fruitType, query)) return;
    reportMissingVariety(fruitType, query);
  }, [fruitType]);

  function addSelectedVariety() {
    if (!fruitType || !listDraftId || selectedVarietyIds.includes(listDraftId) || selectedVarietyIds.length >= 6) return;
    track('planner_variety_added', { variant: `n_${selectedVarietyIds.length + 1}` });
    setSelectedVarietyIds(current => [...current, listDraftId]);
    setListDraftId('');
    setComparePickerResetSignal(current => current + 1);
  }

  function removeSelectedVariety(id: string) {
    setSelectedVarietyIds(current => current.filter(item => item !== id));
    setExistingVarietyIds(current => current.filter(item => item !== id));
  }

  function setVarietyAlreadyOwned(id: string, checked: boolean) {
    setExistingVarietyIds(current => {
      if (checked) {
        return current.includes(id) ? current : [...current, id];
      }

      return current.filter(item => item !== id);
    });
  }

  return (
    <div className="max-w-[1040px] space-y-6">
      <div>
        <Link
          href="/plants"
          className="inline-flex items-center gap-1.5 font-body text-[14px] text-sage hover:text-warm-brown transition-colors duration-200"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 2L4 7L9 12" />
          </svg>
          Back to Plant Library
        </Link>
        <h1 className="font-heading text-[32px] text-dark-text leading-tight mt-4">
          Fruit Tree Planner
        </h1>
        <p className="font-body text-[16px] text-sage mt-2 max-w-[820px] leading-relaxed">
          Plan compatible pollination partners, compare varieties, and understand flowering compatibility for apples, pears, plums, blueberries, cherries, and more.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-sand p-6 space-y-5">
        <FlowStep number="1" title="Choose a fruit type">
          <div className={fruit ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)]' : 'grid gap-4'}>
            <FruitTypePicker value={fruitType} onChange={handleFruitTypeChange} />
            {fruit && (
              <FruitContextCard
                fruitLabel={fruit.label}
                description={fruit.description}
                partnerDistanceNote={fruit.partnerDistanceNote}
                varietyCount={fruit.varieties.length}
              />
            )}
          </div>
        </FlowStep>

        <FlowStep number="2" title="Choose what you want to check">
          <ModePicker value={mode} onChange={handleModeChange} />
        </FlowStep>

        <FlowStep number="3" title={mode === 'single' ? 'Choose your variety' : 'Choose your varieties'}>
          {!fruit ? (
            <div className="rounded-xl border border-sand bg-[#FAFAF9] p-4">
              <p className="font-body text-[14px] text-dark-text leading-relaxed">
                Choose a fruit type to begin. Willowbottom will then load matching varieties, partner suggestions, and pair checks for that crop.
              </p>
            </div>
          ) : mode === 'single' ? (
            <>
              {fruitType === 'honeyberry' && (
                <HoneyberryFilterBar
                  sizeFilter={hbSizeFilter}
                  onSizeChange={setHbSizeFilter}
                  flavorFilter={hbFlavorFilter}
                  onFlavorChange={setHbFlavorFilter}
                  bloomFilter={hbBloomFilter}
                  onBloomChange={setHbBloomFilter}
                  harvestFilter={hbHarvestFilter}
                  onHarvestChange={setHbHarvestFilter}
                  totalCount={varietyOptions.length}
                  filteredCount={filteredVarietyOptions.length}
                />
              )}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_280px] lg:items-start">
                <SearchableCombobox
                  id="fruit-pollination-variety"
                  label="Main variety"
                  value={selectedVarietyId}
                  onChange={value => {
                    handleSelectedVarietyChange(value);
                    if (value === secondVarietyId) setSecondVarietyId('');
                  }}
                  options={filteredVarietyOptions}
                  placeholder={`Search ${fruit.label.toLowerCase()} varieties`}
                  helper="Choose one variety to see the strongest pollination partners."
                  emptyState="No matching varieties yet."
                  reserveAlignedSpace
                  onNoMatch={handleVarietyNotFound}
                />

                <SearchableCombobox
                  id="fruit-pollination-second-variety"
                  label="Optional comparison variety"
                  value={secondVarietyId}
                  onChange={setSecondVarietyId}
                  options={secondVarietyOptions}
                  placeholder="Search a second variety"
                  helper="Optional direct pair check."
                  emptyState="No matching partner varieties yet."
                  reserveAlignedSpace
                  onNoMatch={handleVarietyNotFound}
                />

                <SelectField
                  id="fruit-pollination-zone"
                  label="USDA zone (optional)"
                  value={zoneValue}
                  onChange={handleZoneChange}
                  options={Array.from({ length: 8 }, (_, index) => {
                    const zone = index + 3;
                    return { value: String(zone), label: `Zone ${zone}` };
                  })}
                  placeholder="Use my saved zone or choose one"
                  helper={zoneHelperText}
                  reserveAlignedSpace
                />
              </div>
              <p className="font-body text-[13px] text-sage">
                Pick a variety first, then scroll to Step 4 for ranked partners, warnings, and the optional pair check.
              </p>
            </>
          ) : (
            <>
              {fruitType === 'honeyberry' && (
                <HoneyberryFilterBar
                  sizeFilter={hbSizeFilter}
                  onSizeChange={setHbSizeFilter}
                  flavorFilter={hbFlavorFilter}
                  onFlavorChange={setHbFlavorFilter}
                  bloomFilter={hbBloomFilter}
                  onBloomChange={setHbBloomFilter}
                  harvestFilter={hbHarvestFilter}
                  onHarvestChange={setHbHarvestFilter}
                  totalCount={varietyOptions.length}
                  filteredCount={filteredVarietyOptions.length}
                />
              )}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
                <ListBuilder
                  label="Varieties to compare"
                  helper="Search and add up to 6 varieties, then mark any you already have."
                  currentValue={listDraftId}
                  onCurrentValueChange={setListDraftId}
                  onAdd={addSelectedVariety}
                  selectedItems={selectedItems}
                  onToggleAlreadyOwned={setVarietyAlreadyOwned}
                  onRemove={removeSelectedVariety}
                  options={listOptions}
                  disabled={!fruit}
                  maxSelected={6}
                  resetSignal={comparePickerResetSignal}
                  onNoMatch={handleVarietyNotFound}
                />
                <SelectField
                  id="fruit-pollination-zone"
                  label="USDA zone (optional)"
                  value={zoneValue}
                  onChange={handleZoneChange}
                  options={Array.from({ length: 8 }, (_, index) => {
                    const zone = index + 3;
                    return { value: String(zone), label: `Zone ${zone}` };
                  })}
                  placeholder="Use my saved zone or choose one"
                  helper={zoneHelperText}
                  reserveAlignedSpace
                />
              </div>
              <p className="font-body text-[13px] text-sage">
                Add the varieties you already have or can buy, then mark any existing trees so Willowbottom can prioritize partners for them.
              </p>
            </>
          )}
        </FlowStep>
      </section>

      {fruit && ((mode === 'single' && singleResult) || (mode === 'compare' && compareResult)) && (
        <div>
          <p className="font-body text-[12px] font-semibold uppercase tracking-wide text-sage">Step 4</p>
          <h2 className="font-heading text-[24px] text-dark-text mt-1">See recommendations</h2>
        </div>
      )}

      {fruit && mode === 'single' && singleResult && (
        <SingleModeResults
          result={singleResult}
          explanation={explanation}
          zone={currentZone}
        />
      )}

      {fruit && mode === 'compare' && compareResult && (
        <CompareModeResults
          result={compareResult}
          explanation={explanation}
          zone={currentZone}
          isPro={isPro}
        />
      )}

      {fruit && ((mode === 'single' && singleResult) || (mode === 'compare' && compareResult)) && (
        <Step4ArticleLinks />
      )}

      <VarietyIndex />
    </div>
  );
}
